import { useState } from 'react'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { absDiffGrams, formatGrams } from '@/lib/weight'
import {
  Banner,
  BilingualLabel,
  Button,
  ChoiceGroup,
  Field,
  KgInput,
  PhotoCapture,
  PickerSheet,
  Sheet,
} from '@/ui'
import { useCodeLabel } from '../../components/labels'
import { bagWeight, lotIdOf, openDispute, type DraftDispute } from '../../model/delivery'
import { reweighEffect } from '../../model/figures'
import {
  moistureFigure,
  parsePctTenths,
  readingInRange,
  tenthsToString,
} from '../../model/moisture'
import { useWizard } from './context'
import { PctInput } from './StepMoisture'
import { WeightField } from './WeightEntry'
import { useWeightEntry } from './hooks'
import { BiText } from '../../components/GateScreen'

type About = DraftDispute['about']

/**
 * F4-B2 — असहमति · Dispute → दोबारा जाँच · Re-check → नतीजा · Outcome. Recorded in a few taps;
 * the operator cannot refuse or decide it. Every value is kept; the dispute travels inside
 * commit_delivery (or void_delivery).
 */
export function DisputeFlow({
  prefill,
  onCancelForm,
}: {
  prefill: Partial<Pick<DraftDispute, 'about' | 'container_id' | 'bag_position'>> | null
  onCancelForm: () => void
}) {
  const { d } = useWizard()
  const active = openDispute(d)
  if (active) {
    const rechecked = active.recheck_measurement_ids.length > 0 || active.photo_ids.length > 0
    return rechecked ? <OutcomeSheet dispute={active} /> : <RecheckSheet dispute={active} />
  }
  if (prefill) return <FormSheet prefill={prefill} onClose={onCancelForm} />
  return null
}

function FormSheet({
  prefill,
  onClose,
}: {
  prefill: Partial<Pick<DraftDispute, 'about' | 'container_id' | 'bag_position'>>
  onClose: () => void
}) {
  const { t } = useI18n()
  const { d, dispatch, roster, stamp } = useWizard()
  const code = useCodeLabel()
  const [about, setAbout] = useState<About | null>(prefill.about ?? null)
  const [bagId, setBagId] = useState<string | null>(prefill.container_id ?? null)
  const [position, setPosition] = useState<number | null>(prefill.bag_position ?? null)
  const [reason, setReason] = useState<string | null>(null)
  const reasons = about ? roster.config.dispute_reasons[about] : []
  const redBags = d.bags.filter((b) => b.red)
  const abouts: About[] = [
    'WEIGHT',
    'GRADE',
    ...(d.moisture.readings.length > 0 ? (['MOISTURE'] as const) : []),
  ]
  const bagOk = about === 'WEIGHT' ? !!bagId : about === 'GRADE' ? position !== null : true
  return (
    <Sheet
      open
      onClose={onClose}
      title={<BilingualLabel k="gate.dispute.title" />}
      footer={
        <Button
          size="touch"
          fullWidth
          disabled={!about || !reason || !bagOk}
          onClick={() => {
            if (!about || !reason) return
            const gradeBag =
              about === 'GRADE' && position !== null ? (redBags[position - 1] ?? null) : null
            const bag = about === 'WEIGHT' ? d.bags.find((b) => b.id === bagId) : gradeBag
            dispatch({
              type: 'dispute_raise',
              dispute: {
                id: newId(),
                about,
                reason_code: reason,
                raised_at: stamp(),
                container_id: bag?.id ?? null,
                bag_position: about === 'GRADE' ? position : null,
                disputed_measurement_id:
                  about === 'WEIGHT' && bag ? (bagWeight(bag)?.id ?? null) : null,
                recheck_measurement_ids: [],
                photo_ids: [],
                test_weight: null,
                chosen_by_farmer: null,
                outcome: null,
                resolution_code: null,
                closed_at: null,
                return_step: d.step,
              },
            })
            onClose()
          }}
        >
          <BiText k="gate.dispute.record" />
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('gate.dispute.aboutLabel')} required>
          <ChoiceGroup
            layout="stack"
            options={abouts.map((a) => ({ value: a, label: t(`gate.dispute.about.${a}`) }))}
            value={about}
            onChange={(a) => {
              setAbout(a)
              setReason(null)
            }}
          />
        </Field>
        {about === 'WEIGHT' && (
          <Field label={t('gate.dispute.whichBag')} required>
            <PickerSheet
              title={t('gate.dispute.whichBag')}
              value={bagId}
              onChange={setBagId}
              options={d.bags
                .filter((b) => !b.withdrawn)
                .map((b) => ({
                  value: b.id,
                  label: b.qr_payload,
                  meta: (
                    <span className="font-mono">{formatGrams(bagWeight(b)?.gross_g ?? 0)}</span>
                  ),
                }))}
            />
          </Field>
        )}
        {about === 'GRADE' && (
          <Field label={t('gate.dispute.whichFailed')} required>
            <ChoiceGroup
              layout="stack"
              options={d.failed_bags.map((f, i) => ({
                value: String(i + 1),
                label: `${t('gate.check.failedN', { n: i + 1 })} · ${code(f.reason_code)}`,
              }))}
              value={position === null ? null : String(position)}
              onChange={(v) => setPosition(Number(v))}
            />
          </Field>
        )}
        {about && (
          <Field label={t('gate.dispute.reason')} required>
            <PickerSheet
              title={t('gate.dispute.reason')}
              value={reason}
              onChange={setReason}
              options={reasons.map((r) => ({ value: r.code, label: code(r) }))}
            />
          </Field>
        )}
      </div>
    </Sheet>
  )
}

function RecheckSheet({ dispute }: { dispute: DraftDispute }) {
  const { t } = useI18n()
  const { d, dispatch, roster, stamp } = useWizard()
  const weight = useWeightEntry(roster.config.weight_min_g, roster.config.weight_max_g)
  const [testRead, setTestRead] = useState<number | null>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [scalePhoto, setScalePhoto] = useState<string | null>(null)
  const [moisturePhotos, setMoisturePhotos] = useState<(string | null)[]>([null, null])
  const photoRequired = d.reading_photos_required === true
  const [picked, setPicked] = useState(false)
  const [r1, setR1] = useState('')
  const [r2, setR2] = useState('')
  const bag = d.bags.find((b) => b.id === dispute.container_id)
  const first = bag ? bagWeight(bag) : null
  const testG = roster.config.test_weight_g
  const testOff =
    testRead !== null && absDiffGrams(testRead, testG) > roster.config.test_weight_tolerance_g
  const cfg = roster.config
  const readings = [r1, r2].map((s) => (s.trim() ? parsePctTenths(s) : null))
  const readingsOk =
    readings.some((v) => v !== null) &&
    readings.every((v, i) =>
      [r1, r2][i]!.trim() === ''
        ? true
        : v !== null && readingInRange(v, cfg.moisture_min_tenths, cfg.moisture_max_tenths),
    )

  const save = () => {
    const at = stamp()
    if (dispute.about === 'WEIGHT') {
      if (weight.value === null) return
      if (weight.askAgain() || !weight.ready) return
      if (testRead !== null) {
        dispatch({
          type: 'dispute_update',
          id: dispute.id,
          patch: {
            test_weight: { id: newId(), test_weight_g: testG, read_g: testRead, taken_at: at },
          },
        })
      }
      dispatch({
        type: 'dispute_reweigh',
        id: dispute.id,
        weight: {
          id: newId(),
          gross_g: weight.value,
          taken_at: at,
          confirmed_twice: weight.confirmedTwice,
          supersedes_id: null,
          photo_media_id: scalePhoto,
        },
      })
    } else if (dispute.about === 'GRADE') {
      if (photo) dispatch({ type: 'dispute_update', id: dispute.id, patch: { photo_ids: [photo] } })
    } else {
      dispatch({
        type: 'dispute_redraw',
        id: dispute.id,
        readings: readings.flatMap((v, i) =>
          v === null
            ? []
            : [
                {
                  id: newId(),
                  tenths: v,
                  bag_no: null,
                  taken_at: at,
                  chosen_by_farmer: true,
                  photo_media_id: moisturePhotos[i],
                },
              ],
        ),
      })
    }
  }

  const can =
    dispute.about === 'WEIGHT'
      ? weight.value !== null && weight.twoDecimals && !!bag && (!photoRequired || !!scalePhoto)
      : dispute.about === 'GRADE'
        ? !!photo
        : picked &&
          readingsOk &&
          (!photoRequired || readings.every((v, i) => v === null || !!moisturePhotos[i]))

  return (
    <Sheet
      open
      dismissible={false}
      onClose={() => undefined}
      title={<BilingualLabel k="gate.dispute.recheck" />}
      footer={
        <Button size="touch" fullWidth disabled={!can} onClick={save}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {dispute.about === 'WEIGHT' && (
          <>
            <div className="flex justify-between rounded-chip bg-paper-sunk px-3 py-2">
              <span className="text-[14px]">{t('gate.dispute.firstValue')}</span>
              <span className="font-mono">{first ? formatGrams(first.gross_g) : '—'}</span>
            </div>
            <WeightField
              entry={weight}
              label={t('gate.dispute.newWeight')}
              hint={t('gate.dispute.sameBag')}
            />
            <Field label={t('gate.v1.weigh.scalePhoto')} required={photoRequired}>
              <PhotoCapture value={scalePhoto} onChange={setScalePhoto} />
            </Field>
            <Field
              label={t('gate.dispute.testWeight', { kg: formatGrams(testG) })}
              hint={t('gate.dispute.testWeightHint')}
            >
              <KgInput value={testRead} onChange={setTestRead} />
            </Field>
            {testOff && <Banner tone="error">{t('gate.dispute.testOff')}</Banner>}
          </>
        )}
        {dispute.about === 'GRADE' && (
          <>
            <Banner tone="info">{t('gate.dispute.gradeHint')}</Banner>
            <Field label={t('gate.dispute.photo')} required>
              <PhotoCapture value={photo} onChange={setPhoto} />
            </Field>
          </>
        )}
        {dispute.about === 'MOISTURE' && (
          <>
            <ChoiceGroup
              label={t('gate.dispute.farmerPicked')}
              options={[{ value: 'yes', label: t('gate.dispute.farmerPicked') }]}
              value={picked ? 'yes' : null}
              onChange={() => setPicked(!picked)}
            />
            <Field label={t('gate.moisture.readingN', { n: 1 })} required>
              <PctInput value={r1} onChange={setR1} />
            </Field>
            <Field label={t('gate.v1.moisture.photo')} required={photoRequired}>
              <PhotoCapture
                value={moisturePhotos[0] ?? null}
                onChange={(value) =>
                  setMoisturePhotos((photos) => photos.map((p, i) => (i === 0 ? value : p)))
                }
              />
            </Field>
            <Field label={t('gate.moisture.readingN', { n: 2 })}>
              <PctInput value={r2} onChange={setR2} />
            </Field>
            {r2.trim() !== '' && (
              <Field label={t('gate.v1.moisture.photo')} required={photoRequired}>
                <PhotoCapture
                  value={moisturePhotos[1] ?? null}
                  onChange={(value) =>
                    setMoisturePhotos((photos) => photos.map((p, i) => (i === 1 ? value : p)))
                  }
                />
              </Field>
            )}
          </>
        )}
      </div>
    </Sheet>
  )
}

function OutcomeSheet({ dispute }: { dispute: DraftDispute }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const { d, dispatch, cards, stamp } = useWizard()
  const bag = d.bags.find((b) => b.id === dispute.container_id)
  const outcome = (o: 'ACCEPTED_AFTER_RECHECK' | 'BAG_WITHDRAWN' | 'COMMITTED_DISPUTED') =>
    dispatch({
      type: 'dispute_outcome',
      id: dispute.id,
      outcome: o,
      resolution_code:
        o === 'BAG_WITHDRAWN'
          ? 'BAG_WITHDRAWN'
          : o === 'COMMITTED_DISPUTED'
            ? null
            : dispute.about === 'GRADE'
              ? 'ORIGINAL_STANDS'
              : 'RECHECK_VALUE_STANDS',
      at: stamp(),
    })

  let values: string[]
  let now: string
  let money: { before: number; after: number } | null = null
  if (dispute.about === 'WEIGHT' && bag) {
    values = bag.weights.map((w) => formatGrams(w.gross_g))
    const cur = bagWeight(bag)
    now = cur ? formatGrams(cur.gross_g) : '—'
    const stream = lotIdOf(d, bag) === d.conv_lot?.id || d.another_field ? 'CONV' : 'MAIN'
    const price = cards[stream]?.price_paise_per_kg
    const firstW =
      bag.weights.find((w) => w.id === dispute.disputed_measurement_id) ?? bag.weights[0]
    if (price != null && firstW && cur)
      money = reweighEffect(firstW.gross_g, cur.gross_g, bag.tare_g, price)
  } else if (dispute.about === 'MOISTURE') {
    values = d.moisture.readings.map((r) => `${tenthsToString(r.tenths)}%`)
    const fig = moistureFigure(d.moisture.readings)
    now = fig === null ? '—' : `${tenthsToString(fig)}%`
    money = { before: 0, after: 0 }
  } else {
    values = [t('gate.dispute.callStands')]
    now = t('gate.dispute.callStands')
  }
  const canWithdraw =
    dispute.about !== 'MOISTURE' && (dispute.container_id !== null || dispute.bag_position !== null)

  return (
    <Sheet
      open
      dismissible={false}
      onClose={() => undefined}
      title={<BilingualLabel k="gate.dispute.outcome" />}
    >
      <div className="flex flex-col gap-3">
        <div>
          <div className="mb-1 text-[13px] font-semibold">{t('gate.dispute.allValues')}</div>
          <ol className="flex flex-col gap-1">
            {values.map((v, i) => (
              <li key={i} className="rounded-chip bg-paper-sunk px-3 py-1.5 font-mono text-[15px]">
                {i + 1}. {v}
              </li>
            ))}
          </ol>
        </div>
        <div className="flex justify-between rounded-card border border-ok/30 bg-ok-wash px-3 py-2">
          <span className="font-semibold">{t('gate.dispute.nowCounts')}</span>
          <span className="font-mono text-[17px]">{now}</span>
        </div>
        {money && (
          <div className="flex justify-between px-1 text-[14px]">
            <span>{t('gate.dispute.money')}</span>
            <span className="font-mono">
              {fmt.money(money.before)} → {fmt.money(money.after)}
            </span>
          </div>
        )}
        {dispute.about === 'GRADE' && <Banner tone="info">{t('gate.dispute.redTieStays')}</Banner>}
        <Button size="xl" fullWidth onClick={() => outcome('ACCEPTED_AFTER_RECHECK')}>
          <BiText k="gate.dispute.settled" />
        </Button>
        {canWithdraw && (
          <Button
            size="touch"
            variant="secondary"
            fullWidth
            onClick={() => outcome('BAG_WITHDRAWN')}
          >
            <BiText k="gate.dispute.bagHome" />
          </Button>
        )}
        <Button
          size="touch"
          variant="secondary"
          fullWidth
          onClick={() => outcome('COMMITTED_DISPUTED')}
        >
          <BiText k="gate.dispute.closeHeld" />
        </Button>
      </div>
    </Sheet>
  )
}
